// Skills v2 — Quality & Improve pure formatting logic (2026-08-19).
//
// Zero React. Every user-facing string is outcome-first, no jargon. The
// mockup is the conformance spec: docs/design/skills-framework-v2-mockup.html
// <section id="s7"> — the four-signal "What the feedback says" strip + overall
// health. All logic that shapes QualitySection lives here so it's unit-testable
// to 100% and the component stays a thin renderer.
//
// Backend contract — GET /api/agents/{id}/skills/quality → SkillQualityOut:
//   { agent_id, total, by_type, positive, negative, neutral, net_score,
//     health, confidence, decayed, attached_skills }
//   by_type = { execution:{positive,negative,neutral}, judge:{...},
//               csat:{...}, human:{...} }
//   health ∈ "healthy" | "needs_review" | "insufficient_data"

// Positive-rate percent for a feedback bucket: round(100 * pos / (pos+neg+neu)).
// Returns null when the bucket is missing or has zero total (nothing to rate).
export function positiveRate(bucket) {
  if (!bucket || typeof bucket !== "object") return null;
  const pos = Number(bucket.positive) || 0;
  const neg = Number(bucket.negative) || 0;
  const neu = Number(bucket.neutral) || 0;
  const total = pos + neg + neu;
  if (total <= 0) return null;
  return Math.round((100 * pos) / total);
}

// Tone for a positive-rate: ok ≥ 60, warn ≤ 40, dim in between, dim when null.
export function rateTone(rate) {
  if (rate === null || rate === undefined) return "dim";
  if (rate >= 60) return "ok";
  if (rate <= 40) return "warn";
  return "dim";
}

// The four mockup signal cards, in order. execution/judge/csat report a
// positive-rate percent; human (your team) reports the positive COUNT of
// corrections you kept.
export function signalReadings(byType) {
  const bt = byType && typeof byType === "object" ? byType : {};

  const rateCard = (key, icon, label) => {
    const rate = positiveRate(bt[key]);
    return { key, icon, label, value: rate, unit: "%", tone: rateTone(rate) };
  };

  const humanBucket = bt.human && typeof bt.human === "object" ? bt.human : {};
  const kept = Number(humanBucket.positive) || 0;

  return [
    rateCard("execution", "✅", "Did it hold up"),
    rateCard("judge", "⚖️", "Graded quality"),
    rateCard("csat", "🙂", "Customers"),
    { key: "human", icon: "🧑", label: "Your team", value: kept, unit: "kept", tone: kept > 0 ? "ok" : "dim" },
  ];
}

// Overall health pill. insufficient_data wins first; then needs_review OR a
// decayed lift → "Needs a look"; otherwise healthy.
export function healthLabel(health, decayed) {
  if (health === "insufficient_data") return { label: "Not enough data yet", tone: "dim" };
  if (health === "needs_review" || decayed) return { label: "Needs a look", tone: "warn" };
  return { label: "Healthy", tone: "ok" };
}

// 92 → "92%"; null/undefined/non-number → "—".
export function formatPct(n) {
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  return `${Math.round(n)}%`;
}

// tone → hex, with the memory-rule fallbacks (no bare COLORS.red / COLORS.green).
export function toneColor(tone, colors) {
  const c = colors || {};
  if (tone === "ok") return c.green || "#10b981";
  if (tone === "warn") return c.orange || "#f59e0b";
  return c.textDim || "#9090a8";
}
